import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../config/firebase';
import { LEGAL } from '../config/legal';

type Outcome = { circleClosed: boolean; caregiversDetached: number };

/**
 * Public account-deletion page.
 *
 * Google Play and Apple both require a web URL where a user can request deletion
 * of their account and data, reachable without installing the app. Signed in, it
 * does the deletion here; signed out, it explains how and offers the email route
 * for anyone who can no longer sign in.
 */
export default function DeleteAccount() {
  const { t } = useTranslation();
  const { currentUser, userProfile } = useAuth();

  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const isCareRecipient = userProfile?.role === 'care_recipient';

  async function handleDelete(e: React.FormEvent) {
    e.preventDefault();
    if (!currentUser?.email) return;

    setBusy(true);
    setError(null);
    try {
      // The server refuses a sign-in older than five minutes, so prove the
      // password now; it also doubles as confirmation of an irreversible action.
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);

      const call = httpsCallable<Record<string, never>, Outcome & { success: boolean }>(
        functions,
        'deleteMyAccount'
      );
      const res = await call({});
      if (!res.data?.success) throw new Error('incomplete');
      setOutcome({
        circleClosed: !!res.data.circleClosed,
        caregiversDetached: res.data.caregiversDetached ?? 0,
      });
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setError(t('deleteAccount.errorPassword'));
      } else if (code === 'auth/network-request-failed') {
        setError(t('deleteAccount.errorNetwork'));
      } else {
        setError(t('deleteAccount.errorGeneric'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (outcome) {
    return (
      <div className="delete-page">
        <h1>{t('deleteAccount.doneTitle')}</h1>
        <p>
          {outcome.circleClosed
            ? t('deleteAccount.doneCircleClosed')
            : t('deleteAccount.doneBody')}
        </p>
        <p>
          <Link to="/">{t('deleteAccount.backHome')}</Link>
        </p>
        <DeleteAccountStyles />
      </div>
    );
  }

  return (
    <div className="delete-page">
      <h1>{t('deleteAccount.title')}</h1>
      <p>{t('deleteAccount.intro')}</p>

      <h2>{t('deleteAccount.whatIsDeletedTitle')}</h2>
      <ul>
        <li>{t('deleteAccount.itemAccount')}</li>
        <li>{t('deleteAccount.itemContent')}</li>
        <li>{t('deleteAccount.itemCalls')}</li>
        <li>{t('deleteAccount.itemCircle')}</li>
      </ul>
      <p>{t('deleteAccount.deviceOnlyNote')}</p>
      <p>{t('deleteAccount.retentionNote')}</p>

      {currentUser ? (
        <form onSubmit={handleDelete} className="delete-account-form">
          <h2>{t('deleteAccount.confirmTitle')}</h2>
          <p>
            {isCareRecipient
              ? t('deleteAccount.warningRecipient')
              : t('deleteAccount.warningCaregiver')}
          </p>
          <p>
            <strong>{t('deleteAccount.cannotUndo')}</strong>
          </p>

          <label htmlFor="delete-password">{t('deleteAccount.passwordLabel')}</label>
          <input
            id="delete-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            required
          />

          {error && <p className="form-error">{error}</p>}

          <button type="submit" className="danger-button" disabled={busy || !password}>
            {busy ? t('deleteAccount.deleting') : t('deleteAccount.deleteButton')}
          </button>
        </form>
      ) : (
        <>
          <h2>{t('deleteAccount.signedOutTitle')}</h2>
          <p>{t('deleteAccount.signedOutBody')}</p>
          <p>
            <Link to="/signin" className="signup-link">
              {t('deleteAccount.signInCta')}
            </Link>
          </p>
          <h2>{t('deleteAccount.byEmailTitle')}</h2>
          <p>
            {t('deleteAccount.byEmailBody')}{' '}
            <a href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a>
          </p>
        </>
      )}
      <DeleteAccountStyles />
    </div>
  );
}

/** Scoped styling, matching the approach used by the legal pages. */
function DeleteAccountStyles() {
  return (
    <style>{`
      .delete-page {
        max-width: 780px;
        margin: 48px auto 80px;
        padding: 0 24px;
        color: #1a1a1a;
        font-size: 16px;
        line-height: 1.7;
      }
      .delete-page h1 { font-size: 36px; line-height: 1.2; margin: 0 0 24px; }
      .delete-page h2 { font-size: 22px; margin: 40px 0 12px; line-height: 1.3; }
      .delete-page p { margin: 0 0 16px; }
      .delete-page ul { margin: 0 0 16px; padding-left: 24px; }
      .delete-page li { margin: 8px 0; }
      .delete-page a { color: #4285f4; }
      /* The sign-in CTA is a filled button; keep its own white label rather than
         inheriting the link colour above, which would be blue on blue. */
      .delete-page a.signup-link { color: #fff; }

      .delete-account-form {
        margin-top: 32px;
        padding: 24px;
        border: 2px solid #f5c2c7;
        border-radius: 12px;
        background: #fff5f5;
      }
      .delete-account-form h2 { margin-top: 0; }
      .delete-account-form label {
        display: block;
        font-weight: 600;
        margin-bottom: 8px;
      }
      .delete-account-form input {
        width: 100%;
        padding: 12px;
        font-size: 16px;
        border: 1px solid #ccc;
        border-radius: 8px;
        margin-bottom: 16px;
      }
      .form-error { color: #b3261e; font-weight: 600; }
      .danger-button {
        background: #b3261e;
        color: #fff;
        border: none;
        border-radius: 8px;
        padding: 14px 24px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
      }
      .danger-button:disabled { opacity: 0.55; cursor: not-allowed; }

      @media (max-width: 640px) {
        .delete-page h1 { font-size: 28px; }
        .delete-page { font-size: 15px; }
      }
    `}</style>
  );
}
